// Port of TrailData/Dataset/TrailDataset+Merging.swift.

import { isValidCoordinate } from '../core/geo';
import type { DataProvenance, DatasetMetadata, GeoCoordinate, TerrainGrid, TerrainSample, TrailDataset } from '../core/models';
import { roundHalfAwayFromZero } from '../core/polyline';
import { provenanceKey } from './route';

/**
 * Returns a dataset combining `base` with `other` (Swift `TrailDataset.merging(_:)`).
 *
 * - Records (trails, stages, POIs, protected areas, regulations, coverage) are de-duplicated by `id`; when both
 *   datasets contain an id, `other`'s record wins and keeps the base record's position. Records only present in
 *   `other` are appended in `other`'s order.
 * - Metadata keeps the base identity (id, name, version); `isSynthetic` is true when either side is synthetic
 *   (synthetic data is never masked by merging), provenance lists are united and notices joined.
 * - Terrain: samples are united, `other` winning at identical coordinates; the grid takes `other`'s cell size and,
 *   conservatively, a synthetic provenance whenever either grid is synthetic.
 */
export function mergeDatasets(base: TrailDataset, other: TrailDataset): TrailDataset {
  const merged: TrailDataset = {
    metadata: mergedMetadata(base.metadata, other.metadata),
    trails: mergedByID(base.trails, other.trails),
    stages: mergedByID(base.stages, other.stages),
    pointsOfInterest: mergedByID(base.pointsOfInterest, other.pointsOfInterest),
    protectedAreas: mergedByID(base.protectedAreas, other.protectedAreas),
    regulations: mergedByID(base.regulations, other.regulations),
    regulatoryCoverage: mergedByID(base.regulatoryCoverage, other.regulatoryCoverage),
  };
  const terrain = mergedTerrain(base.terrain, other.terrain);
  if (terrain) merged.terrain = terrain;
  return merged;
}

export function mergedByID<T extends { id: string }>(base: readonly T[], overrides: readonly T[]): T[] {
  const overrideByID = new Map<string, T>();
  for (const item of overrides) overrideByID.set(item.id, item); // last occurrence in `overrides` wins
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of base) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(overrideByID.get(item.id) ?? item);
  }
  for (const item of overrides) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(overrideByID.get(item.id) ?? item);
  }
  return out;
}

export function mergedMetadata(base: DatasetMetadata, other: DatasetMetadata): DatasetMetadata {
  const provenance: DataProvenance[] = [];
  const seen = new Set<string>();
  for (const p of [...base.provenance, ...other.provenance]) {
    const key = provenanceKey(p);
    if (seen.has(key)) continue;
    seen.add(key);
    provenance.push(p);
  }
  const notices: string[] = [];
  for (const notice of [base.notice, other.notice]) {
    if (notice !== undefined && !notices.includes(notice)) notices.push(notice);
  }
  const metadata: DatasetMetadata = {
    id: base.id,
    name: base.name,
    version: base.version,
    generatedAt: Math.max(base.generatedAt, other.generatedAt),
    isSynthetic: base.isSynthetic || other.isSynthetic,
    provenance,
  };
  if (notices.length > 0) metadata.notice = notices.join('\n');
  return metadata;
}

/** Micro-degree rounding (~0.1 m) identifies the same grid cell centre across datasets. */
function terrainKey(c: GeoCoordinate): string {
  return `${roundHalfAwayFromZero(c.latitude * 1_000_000)},${roundHalfAwayFromZero(c.longitude * 1_000_000)}`;
}

export function mergedTerrain(base: TerrainGrid | undefined, other: TerrainGrid | undefined): TerrainGrid | undefined {
  if (!base) return other;
  if (!other) return base;
  const overridden = new Map<string, TerrainSample>();
  for (const sample of other.samples) {
    if (isValidCoordinate(sample.coordinate)) overridden.set(terrainKey(sample.coordinate), sample);
  }
  const samples: TerrainSample[] = [];
  const seen = new Set<string>();
  for (const list of [base.samples, other.samples]) {
    for (const sample of list) {
      if (!isValidCoordinate(sample.coordinate)) continue;
      const key = terrainKey(sample.coordinate);
      if (seen.has(key)) continue;
      seen.add(key);
      samples.push(overridden.get(key) ?? sample);
    }
  }
  const provenance = base.provenance.isSynthetic && !other.provenance.isSynthetic ? base.provenance : other.provenance;
  return { cellSizeMeters: other.cellSizeMeters, samples, provenance };
}
